import React, {useCallback, useContext, useMemo} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCodeCompare} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

// Contexts
import {SettingsContext} from "../../contexts/settings";

// Utils
import {prettyDateWithDayInUserTimezone} from "../../utils/helpers";

// Components
import Accordion from "../../components/Accordion";

// Style
import {voice} from "../../style/components/typography";
import {border, pad, radius} from "../../style/components/variables";
import {Button, Loader, NotLoaded, scrollbar, Small, Text} from "../../style/components/general";

const RecordHistory = ({
  record,
  versions,
  currentVersion,
  setCurrentVersion,
  viewModification,
  setViewModification,
  edit = false
}) => {
  const {settings} = useContext(SettingsContext);

  const versionDescription = useCallback((version, targetVersion) => {
    // handles artificial cases
    if (version.artificialCase === "OVERDUE") {
      return targetVersion.draft
        ? "Incomplete submission marked overdue by Hero Builder"
        : "Overdue record generated by Hero Builder";
    }
    if (version.artificialCase === "ARCHIVED") {
      const userString = version.user
        ? ` by ${version.user.firstName} ${version.user.lastName}`
        : "";
      return `Archived${userString}`;
    }

    if (version.artificialCase === "ORIGINAL") {
      let action = "Submission";
      if (version.overdue && version.draft) action = "Overdue incomplete submission";
      if (version.draft) action = "Incomplete submission";
      if (version.overdue) action = "Overdue submission";
      const userString = version.user
        ? ` by ${version.user.firstName} ${version.user.lastName}`
        : "";
      return `${action} created${userString}`;
    }

    let message = "";
    if (!version.overdue && version.draft && !targetVersion.overdue && !targetVersion.draft)
      message += "Submission created";
    else if (version.draft && !version.overdue) message += "Incomplete submission modified";
    else if (!version.draft && version.overdue && targetVersion.overdue && targetVersion.draft)
      message += "Overdue incomplete submission created";
    else if (version.draft && version.overdue && targetVersion.draft)
      message += "Overdue incomplete submission modified";
    else if (version.overdue && !targetVersion.draft) message += "Overdue submission created";
    else message += "Modified";

    const userString = targetVersion.user
      ? ` by ${targetVersion.user.firstName} ${targetVersion.user.lastName}`
      : "";
    return `${message}${userString}`;
  }, []);

  const extendedVersions = useMemo(() => {
    const tempVersions = [];
    const realVersions = versions ?? [];

    let insertedOverdue = false;
    let insertedArchived = false;

    const insertArchived = (version, earliest) => {
      insertedArchived = true;

      let overdueSubmission = version
        ? {
            ...version.submission,
            responses: {...version.submission.responses, ...version.modification}
          }
        : null;

      if (earliest && overdueSubmission) {
        const {responses} = overdueSubmission;
        overdueSubmission = {
          ...overdueSubmission,
          responses: Object.fromEntries(
            Object.entries(responses)
              .filter(([, value]) => value === null || typeof value !== "object")
              .map(([key]) => [key, ""])
          )
        };
      }

      tempVersions.push({
        id: -1,
        user: record.archivedBy,
        submission: overdueSubmission,
        modification: null,
        createdAt: record.archivedAt,
        artificialCase: "ARCHIVED"
      });
    };

    const insertOverdue = (version, earliest = false) => {
      insertedOverdue = true;

      let overdueSubmission = version
        ? {
            ...version.submission,
            responses: {...version.submission.responses, ...version.modification}
          }
        : null;

      if (earliest && overdueSubmission) {
        const {responses} = overdueSubmission;
        overdueSubmission = {
          ...overdueSubmission,
          responses: Object.fromEntries(
            Object.entries(responses)
              .filter(([, value]) => value === null || typeof value !== "object")
              .map(([key]) => [key, ""])
          )
        };
      }

      tempVersions.push({
        id: -2,
        user: version.user,
        submission: overdueSubmission,
        modification: null,
        createdAt: record.dateDue,
        artificialCase: "OVERDUE",
        overdue: true,
        draft: version?.draft || false
      });
    };

    // need to figure out how to insert the overdue version not right when the due date is before the version date, but rather, at the last one for which this is true
    const getShouldInsertArchived = version =>
      !insertedArchived &&
      !record.completedAt &&
      record.archived &&
      record.archivedAt &&
      (!version || version.createdAt) &&
      dayjs(record.archivedAt).isBefore(dayjs(version?.createdAt));

    const getShouldInsertOverdue = (version, nextVersion) =>
      !insertedOverdue &&
      record.dateDue &&
      (record.overdue || dayjs(record.createdAt).isAfter(dayjs(record.dateDue))) &&
      (nextVersion === null ||
        (nextVersion &&
          version &&
          dayjs(record.dateDue).isAfter(dayjs(nextVersion.createdAt)) &&
          dayjs(record.dateDue).isBefore(dayjs(version.createdAt))));

    const getInitialVersion = () => {
      const versionsWithUsers = realVersions.filter(v => v.user);
      const targetUser =
        versionsWithUsers?.length > 0
          ? versionsWithUsers[versionsWithUsers.length - 1]?.user
          : record?.user;
      const target = realVersions?.length > 0 ? realVersions[realVersions.length - 1] : record;

      let createdAt = record.originalSubmissionAt;
      if (!createdAt && (record.draft || realVersions.length === 0)) createdAt = record.createdAt;

      if (!target?.submission && realVersions.length > 1) return null;

      let targetSubmission = target?.submission;
      if (!targetSubmission) targetSubmission = record.submission;

      return {
        id: -3,
        user: targetUser || null,
        submission: {
          ...targetSubmission,
          responses: {...targetSubmission.responses, ...target.modification}
        },
        modification: null,
        createdAt,
        draft: target.draft,
        overdue:
          (target.overdue ||
            (target.dateDue && dayjs(target.createdAt).isAfter(dayjs(target.dateDue)))) &&
          !insertedOverdue,
        artificialCase: "ORIGINAL"
      };
    };

    const insertWithOverdueArchivedVersionsIfNeeded = (
      version,
      nextVersion,
      earliest = false,
      forceInsertOverdue = false
    ) => {
      const shouldInsertArchived = getShouldInsertArchived(version);
      const shouldInsertOverdue =
        forceInsertOverdue || getShouldInsertOverdue(version, nextVersion);

      const order = [];

      if (!version?.createdAt) {
        if (shouldInsertArchived) order.push("ARCHIVED");
        if (shouldInsertOverdue) order.push("OVERDUE");
        if (version) order.push("CURRENT");
      } else {
        let insertedCurrent = false;
        if (
          version &&
          shouldInsertArchived &&
          dayjs(version.createdAt).isAfter(dayjs(record.archivedAt))
        ) {
          order.push("CURRENT");
          insertedCurrent = true;
        }
        if (shouldInsertArchived) order.push("ARCHIVED");
        if (
          !insertedCurrent &&
          version &&
          shouldInsertOverdue &&
          dayjs(version.createdAt).isAfter(dayjs(record.dateDue))
        ) {
          order.push("CURRENT");
          insertedCurrent = true;
        }
        if (shouldInsertOverdue) order.push("OVERDUE");
        if (!insertedCurrent && version) order.push("CURRENT");
      }

      order.forEach(o => {
        if (o === "ARCHIVED") insertArchived(version || record, earliest);
        if (o === "OVERDUE") insertOverdue(version || record, earliest);
        if (o === "CURRENT")
          tempVersions.push({
            ...version,
            modification:
              (earliest && shouldInsertOverdue) ||
              (version.modification && Object.values(version.modification).every(val => !val)) ||
              (version && !version.createdAt)
                ? null
                : version.modification
          });
      });
    };

    realVersions.forEach((v, i) =>
      insertWithOverdueArchivedVersionsIfNeeded(v, realVersions[i + 1])
    );

    // if no versions exist and the record is overdue, then we'd want to omit the initial version
    const initialVersion =
      (realVersions.length > 0 && realVersions.filter(({user}) => user).length === 0) ||
      (record.overdue && realVersions.length === 0)
        ? null
        : getInitialVersion();

    insertWithOverdueArchivedVersionsIfNeeded(
      initialVersion,
      null,
      true,
      realVersions.length === 0 && record.overdue
    );

    return tempVersions;
  }, [record, versions]);

  const buildVersions = useCallback(() => {
    const rendered = extendedVersions?.map((version, idx) => {
      // Targets the record if the more recent version doesn't exist
      const targetVersion = idx > 0 ? extendedVersions[idx - 1] : record;

      return (
        <Accordion
          testId="record.historyEntry"
          key={`version-${version.id}`}
          label={
            version.createdAt
              ? prettyDateWithDayInUserTimezone(version.createdAt, settings.timezone)
              : "Initial Submission"
          }
          handleOpen={() => {
            setViewModification(false);
            setCurrentVersion(prev =>
              prev?.id !== version.id
                ? {
                    ...version,
                    user: (version.artificialCase ? version.user : targetVersion.user) || null
                  }
                : null
            );
          }}
          toggleOpen={currentVersion?.id && currentVersion.id === version.id}
          disabled={edit}>
          <>
            <AccordionContent>
              {version.submission ? (
                <>
                  <Small secondary>{versionDescription(version, targetVersion)}</Small>
                  {version.modification && (
                    <Mod
                      type="button"
                      disabled={edit}
                      onClick={() => setViewModification(prev => !prev)}
                      isActive={viewModification}
                      data-testid="record.viewModification">
                      <FontAwesomeIcon icon={faCodeCompare} />
                    </Mod>
                  )}
                </>
              ) : (
                <Small secondary>
                  Modified completed by user{" "}
                  {version.user && (
                    <>
                      from {version.user.firstName}&nbsp;
                      {version.user.lastName}{" "}
                    </>
                  )}
                  to {targetVersion.user.firstName}&nbsp;
                  {targetVersion.user.lastName}
                </Small>
              )}
            </AccordionContent>
            {version.note && (
              <Explanation quiet>
                <strong>Explanation: </strong>
                {version.note}
              </Explanation>
            )}
          </>
        </Accordion>
      );
    });

    return rendered;
  }, [
    currentVersion,
    edit,
    record,
    setCurrentVersion,
    setViewModification,
    settings,
    versionDescription,
    extendedVersions,
    viewModification
  ]);

  return (
    <HistoryWrapper>
      <Text>History</Text>
      <hr />
      <VersionsWrapper>
        {versions ? (
          buildVersions()
        ) : (
          <VersionsNotLoaded>
            <VersionLoader />
          </VersionsNotLoaded>
        )}
      </VersionsWrapper>
    </HistoryWrapper>
  );
};

RecordHistory.propTypes = {
  record: PropTypes.objectOf(PropTypes.any).isRequired,
  versions: PropTypes.arrayOf(PropTypes.any),
  currentVersion: PropTypes.objectOf(PropTypes.any),
  setCurrentVersion: PropTypes.func.isRequired,
  viewModification: PropTypes.bool,
  setViewModification: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

// Style Overrides
const HistoryWrapper = styled.div`
  border: 2px solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  padding: ${pad}px;
  margin-bottom: ${pad}px;

  @media print {
    border: none;
  }
`;

const VersionsWrapper = styled.div`
  max-height: 350px;
  min-height: min-content;
  margin-bottom: ${pad}px;
  overflow-y: auto;
  padding: ${pad / 2}px;
  ${scrollbar};
`;

const AccordionContent = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 ${pad / 2}px ${pad * 2}px;
  border: ${border} solid ${({theme}) => theme.tertiary};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Mod = styled(Button)`
  ${voice.quiet};
  padding: ${pad / 4}px ${pad / 2}px;
  margin-left: ${pad / 2}px;
  text-align: left;
  pointer-events: ${({disabled}) => (!disabled ? "pointer" : "none")};
  opacity: ${({isActive}) => (!isActive ? 1 : 0.5)};
`;

const VersionsNotLoaded = styled(NotLoaded)`
  height: 15px;
  width: calc(100% - 1px);
  margin: ${pad / 2}px 0 0 0;
`;

const VersionLoader = styled(Loader)`
  height: min-content;
  width: calc(100% - 1px);
  margin: 0;
`;

const Explanation = styled(Text)`
  margin-left: 22px;
`;

export default RecordHistory;
